import { useEffect, useState, type ReactNode } from "react";
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal as RNModal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
  type LayoutChangeEvent,
} from "react-native";

import { busyAria } from "../a11y";
import { Sheet } from "./Sheet";
import type { UiTheme } from "../theme";

// Full-screen page-sheet chrome — opaque, slide-up, iOS `pageSheet`
// presentation. Encodes the `Cancel · Title · Action` header + optional
// keyboard-avoiding body ONCE for the 9 hand-rolled page-sheets (PlateCalc,
// Feedback, LanguagePicker, OutboxDetail, ExerciseSwapPicker, EntryTip,
// AudioSettings, EditMaxSheet, and ExercisePicker's inline header — see
// next/consolidation.md).
//
// The header title is centred by a symmetric 3-cell flex layout (left/right
// cells grow equally), which ENDS the fixed-`headerSpacer`-width fake-centering
// bug (the disagreeing 32/44/56/64 px spacers `next/consolidation.md` names) —
// no spacer to keep in sync with the Cancel label's width.
//
// Safe-area insets are injected as props (`topInset`/`bottomInset`), not read
// from react-native-safe-area-context — @heraion/ui carries no such dep, so
// the mount feeds useSafeAreaInsets() (same seam as Maxes/EditMaxSheet). Web
// passes none. RN Modal + KeyboardAvoidingView both work under react-native-web,
// so one tree serves mobile and the web-rnw console.

export type PageSheetAction = {
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export type PageSheetProps = {
  theme: UiTheme;
  visible: boolean;
  onRequestClose: () => void;
  title: string;
  /** Leading dismiss affordance label (e.g. "Cancel" / "Close"). */
  cancelLabel: string;
  /** Defaults to onRequestClose. */
  onCancel?: () => void;
  cancelDisabled?: boolean;
  /** Optional trailing action (e.g. Save / Apply). Omit → balanced spacer. */
  action?: PageSheetAction;
  /** Wrap the body in a KeyboardAvoidingView (forms with text input). */
  keyboardAvoiding?: boolean;
  /** How much room the sheet takes. `"full"` (default) is the page-sheet the
   *  other eight callers have always had. `"compact"` anchors a card to the
   *  bottom, sized to its content — for a sheet whose body is one field.
   *
   *  ⛔ Opt-in, so the eight untouched callers render byte-identically: a
   *  default flip here would be a silent redesign of nine surfaces.
   *  Antenore, 2026-09-02, on the MAX editor: «tanto che ci sei lo riduci in
   *  taglia, usa tutto lo schermo inutilmente». */
  size?: "full" | "compact";
  /** Safe-area insets (px) from the mount. topInset: native leaves 0 (the Modal
   *  already starts below the status bar); it is for web. */
  topInset?: number;
  bottomInset?: number;
  children: ReactNode;
};

export function PageSheet({
  theme,
  visible,
  onRequestClose,
  title,
  cancelLabel,
  onCancel,
  cancelDisabled = false,
  action,
  keyboardAvoiding = false,
  topInset = 0,
  bottomInset = 0,
  size = "full",
  children,
}: PageSheetProps) {
  const compact = size === "compact";
  const styles = makeStyles(theme);
  // Stack the title under the buttons once it wraps: the Cancel label is the
  // one-line height reference. One-way latch, as stacking un-wraps the title;
  // reset when the strings change.
  const { width, fontScale } = useWindowDimensions();
  const [lineH, setLineH] = useState(0);
  const [titleH, setTitleH] = useState(0);
  const [stacked, setStacked] = useState(false);
  useEffect(() => {
    setStacked(false);
    setTitleH(0);
  }, [title, cancelLabel, width, fontScale]);
  useEffect(() => {
    if (!stacked && lineH > 0 && titleH > lineH * 1.5) setStacked(true);
  }, [stacked, lineH, titleH]);
  const titleNode = (
    <Text
      accessibilityRole="header"
      // RNW drops accessibilityRole on some components: `role` reaches the web.
      // Level 2: an open sheet must not add a second h1 to the page.
      role="heading"
      aria-level={2}
      style={[styles.title, stacked && styles.titleStacked]}
      numberOfLines={stacked ? undefined : 2}
      onLayout={
        stacked ? undefined : (e: LayoutChangeEvent) => setTitleH(e.nativeEvent.layout.height)
      }
    >
      {title}
    </Text>
  );
  // ⚠️ Android gets a behaviour only in the COMPACT variant, and the asymmetry
  // is deliberate. The full page-sheet fills the screen, so its field sits far
  // from the keyboard and Android's own adjustResize has always been enough —
  // that is why the eight existing callers pass `undefined` and nobody noticed.
  // The compact card is pinned to the BOTTOM edge and `MaxEditForm` autofocuses,
  // so the keyboard would open exactly where the field is. Raised by the a11y
  // audit of this changeset, and ⛔ NOT verified on a device: it is the first
  // thing to look at in the smoke ([[feedback_layout_refactors_need_device_check]]).
  // ⛔ `flex: 1` ONLY in the full variant. The page-sheet fills the screen, so
  // a flex child grows into it; the compact card is sized BY its content, so a
  // flex:1 child has nothing to grow into and collapses — which is exactly what
  // his smoke of 4.48.0 showed: the field squeezed to a sliver and «Remove MAX»
  // cut in half by the system bar. The bench could not see it (a Modal has no
  // bottom-anchored parent there), the phone could.
  const body = keyboardAvoiding ? (
    <KeyboardAvoidingView
      style={compact ? undefined : styles.flex}
      behavior={
        Platform.OS === "ios" ? "padding" : compact ? "padding" : undefined
      }
    >
      {children}
    </KeyboardAvoidingView>
  ) : (
    children
  );

  // Extracted so both variants render the SAME header — a second copy is how
  // the compact sheet would slowly stop looking like the full one.
  const header = (
    <View style={styles.header}>
      <View style={styles.headerRow}>
        <View style={styles.sideLeft}>
          <Pressable
            accessibilityRole="button"
            onPress={onCancel ?? onRequestClose}
            disabled={cancelDisabled}
            hitSlop={8}
            style={({ pressed }) => [
              styles.hit,
              pressed && !cancelDisabled && styles.pressed,
            ]}
          >
            <Text
              style={[styles.cancel, cancelDisabled && styles.disabledText]}
              onLayout={(e: LayoutChangeEvent) => setLineH(e.nativeEvent.layout.height)}
            >
              {cancelLabel}
            </Text>
          </Pressable>
        </View>
        {stacked ? <View style={styles.flex} /> : titleNode}
        <View style={styles.sideRight}>
          {action ? (
            <Pressable
              accessibilityRole="button"
              // ⚠️ The name has to be DECLARED, not inherited from the child.
              // While `loading` the <Text> below is replaced by a spinner, and
              // a Pressable whose only child is an ActivityIndicator has no
              // accessible name at all — so this button went from "Save" to
              // unnamed exactly when it mattered, on native AND web, across
              // the 9 sheets this primitive backs. <Button> never had this
              // (it declares `accessibilityLabel` outright); this hand-rolled
              // twin did. Found by the a11y audit of RW-38.
              accessibilityLabel={action.label}
              // Same half RW-38 fixed on <Button>; why `aria-*`: see `busyAria`.
              {...busyAria(action.loading)}
              onPress={action.onPress}
              disabled={action.disabled || action.loading}
              hitSlop={8}
              style={({ pressed }) => [
                styles.hit,
                pressed && !(action.disabled || action.loading) && styles.pressed,
              ]}
            >
              {action.loading ? (
                <ActivityIndicator size="small" color={theme.palette.ink2} />
              ) : (
                <Text
                  style={[styles.action, action.disabled && styles.disabledText]}
                >
                  {action.label}
                </Text>
              )}
            </Pressable>
          ) : null}
        </View>
      </View>
      {stacked ? titleNode : null}
    </View>
  );

  // ⛔ Two different overlays, not one parameterised: the full variant is a
  // native page-sheet (`presentationStyle`), the compact one rides the shared
  // scrim. RN refuses `presentationStyle` together with `transparent`, so
  // branching here is the honest shape.
  //
  // ⛔ And the card, the scrim and the tap-out are NOT re-rolled: <Sheet> is
  // already "a bottom-anchored card over the shared <Modal> scrim", and a
  // second one would be the (N+1)-th copy the consolidation track exists to
  // stop. What PageSheet still owns is its Cancel · Title · Action header,
  // which <Sheet> has no notion of, so the title rides there and <Sheet>'s
  // own stays unset.
  if (compact) {
    return (
      <Sheet
        theme={theme}
        visible={visible}
        onRequestClose={onRequestClose}
        bottomInset={bottomInset}
      >
        {header}
        {body}
      </Sheet>
    );
  }

  return (
    <RNModal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onRequestClose}
    >
      <View
        style={[
          styles.container,
          { paddingTop: topInset, paddingBottom: bottomInset },
        ]}
      >
        {header}
        {body}
      </View>
    </RNModal>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: p.bg },
    flex: { flex: 1 },
    header: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s2 },
    // s4 between the cells: at 320 with a large font Cancel and the action
    // fill the row and used to touch; if even that does not fit, the action
    // goes to its own line.
    headerRow: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: s.s4, rowGap: s.s2 },
    // Symmetric side cells → the title is centred regardless of the Cancel /
    // action label widths. No fixed spacer to keep in sync.
    // Never shrink: at a large font scale the title wraps (2 lines) in the
    // room left, it never draws under Cancel / action.
    sideLeft: { flexGrow: 0, flexShrink: 0, alignItems: "flex-start" },
    sideRight: { flexGrow: 0, flexShrink: 0, alignItems: "flex-end" },
    // ≥44 tap height on the Cancel/action links (the compact label stays,
    // hitSlop widens the rest) + a pressed cue, matching Button/IconButton.
    hit: { minHeight: 44, justifyContent: "center" },
    pressed: { opacity: 0.6 },
    title: {
      flex: 1,
      minWidth: 0,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
      textAlign: "center",
    },
    // Own row under the buttons: full width, free to wrap.
    titleStacked: { flex: undefined, alignSelf: "stretch", paddingTop: s.s2 },
    cancel: { color: p.ink2, fontSize: t.sizes.base },
    // Neutral bold (AA-safe on bg) — not brand500, which fails 4.5:1 on the
    // light ground; the header link reads as primary via weight, not hue
    // (matches the "one orange action" colour budget of the old EditMaxSheet).
    action: { color: p.ink, fontSize: t.sizes.base, fontWeight: "700" },
    // Disabled links drop to ink3 (AA on bg) rather than an opacity dim that
    // sinks below 4.5:1.
    disabledText: { color: p.ink3 },
  });
}
